import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import { Percent, Lock, TrendingUp, TrendingDown, BarChart3 } from "lucide-react";
import { motion } from "framer-motion";

const probColor = (p) => (p >= 70 ? "text-bull" : p >= 45 ? "text-amber" : "text-bear");
const probBar = (p) => (p >= 70 ? "bg-[var(--bull)]" : p >= 45 ? "bg-[var(--amber)]" : "bg-[var(--bear)]");

export default function ProbabilitySection() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const locked = !user || !user.membership_active;

  useEffect(() => {
    api.get("/probability").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <section id="probability" className="scroll-mt-20">
      <SectionTitle
        eyebrow="Bandarmology · Pattern Engine"
        title="Probabilitas Top Gainer & Analisis Looser Berulang"
        desc="Mesin pola berulang: saham yang paling sering rebound dari ARB, win-rate menuju top gainer, dan skor probabilitas berbasis akumulasi volume."
        icon={Percent}
      />
      <div className="relative">
        <div className={locked ? "pointer-events-none select-none blur-[6px] opacity-60" : ""}>
          {data ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7 space-y-4">
                <div className="card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={16} className="text-bull" />
                    <h3 className="font-heading font-semibold">Kandidat Top Gainer</h3>
                  </div>
                  <div className="space-y-3">
                    {(showAll ? (data.top_gainer_candidates || []) : (data.top_gainer_candidates || []).slice(0, 3)).map((c, i) => (
                      <motion.div key={i} whileHover={{ x: 3 }} transition={{ duration: 0.15 }} className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]" data-testid={`gainer-cand-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-heading font-bold">{c.ticker}</span>
                            <span className="text-xs text-[var(--text-muted)] ml-2">{c.name}</span>
                          </div>
                          <span className={`font-mono-data font-bold ${probColor(c.probability)}`}>{c.probability}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--surface-card)] overflow-hidden mb-2">
                          <div className={`h-full ${probBar(c.probability)}`} style={{ width: `${c.probability}%` }} />
                        </div>
                        <p className="text-[11px] text-accent font-mono-data">{c.pattern}</p>
                        <p className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-relaxed">{c.reason}</p>
                      </motion.div>
                    ))}
                  </div>
                  {(data.top_gainer_candidates || []).length > 3 && (
                    <button onClick={() => setShowAll((v) => !v)} data-testid="gainers-more" className="mt-3 w-full h-9 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-sm font-semibold text-accent hover:bg-[var(--surface-hover)] transition-colors">
                      {showAll ? "Tampilkan lebih sedikit" : `Selengkapnya (${(data.top_gainer_candidates || []).length - 3} kandidat lainnya)`}
                    </button>
                  )}
                </div>
              </div>
              <div className="lg:col-span-5 space-y-4">
                <div className="card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingDown size={16} className="text-bear" />
                    <h3 className="font-heading font-semibold">Looser Berulang (Potensi Rebound)</h3>
                  </div>
                  <div className="space-y-2.5">
                    {(data.recurring_losers || []).map((l, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-[var(--border-subtle)] pb-2.5 last:border-0" data-testid={`loser-${i}`}>
                        <div>
                          <span className="font-heading font-bold">{l.ticker}</span>
                          <p className="text-[11px] text-[var(--text-muted)]">{l.frequency}</p>
                        </div>
                        <div className="text-right">
                          <span className={`font-mono-data font-bold ${probColor(l.rebound_chance)}`}>{l.rebound_chance}%</span>
                          <p className="text-[10px] text-[var(--text-muted)]">rebound</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 size={16} className="text-accent" />
                    <h3 className="font-heading font-semibold">Frequency Win-Rate</h3>
                  </div>
                  <div className="space-y-2">
                    {(data.frequency_table || []).map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-sm" data-testid={`freq-${i}`}>
                        <span className="font-heading font-semibold w-14">{f.ticker}</span>
                        <span className="font-mono-data text-xs text-[var(--text-muted)]">{f.times_top_gainer}x TG</span>
                        <span className="font-mono-data text-xs text-bull">{f.avg_gain}</span>
                        <span className={`font-mono-data font-bold text-xs ${probColor(f.win_rate)}`}>{f.win_rate}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card p-8 text-center text-[var(--text-secondary)] text-sm">Menghitung probabilitas...</div>
          )}
        </div>

        {locked && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="probability-lock">
            <div className="card p-6 text-center max-w-sm mx-4 border-[var(--gold)]/30">
              <div className="w-12 h-12 rounded-full bg-[var(--gold)]/15 flex items-center justify-center mx-auto mb-3">
                <Lock size={22} className="text-gold" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-1">Fitur VIP Terkunci</h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">Mesin probabilitas top gainer tersedia untuk member FAS Trading. Ambil paket membership untuk membukanya.</p>
              <button onClick={() => document.getElementById("membership")?.scrollIntoView({ behavior: "smooth" })} data-testid="unlock-probability-btn" className="w-full h-10 rounded-lg bg-[var(--gold)] text-[#07090E] font-semibold hover:brightness-110 transition-all">
                Lihat Paket Membership
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
